import { IndividualConfig, ToastrService } from "ngx-toastr";
import { tap } from "rxjs";
import { AppModule } from "../../app.module";
import { HttpErrorResponse } from "@angular/common/http";

interface IToastConfig extends Partial<IndividualConfig> {
  title: string | { [key: number]: string };
}

type IToastText = (
  { message?: string; useResponseAsMessage: true; }
  | { message: string | { [key: number]: string } | { [key: number]: 'auto' }; useResponseAsMessage?: false; }
) & IToastConfig;

export interface IToastOptions {
  success?: IToastText;
  error?: IToastText;
}

/**
 * ngx-toastr docs: https://www.npmjs.com/package/ngx-toastr
 * ngx-toastr playground: https://ngx-toastr.vercel.app/
 * 
 * To use this decorator, just put it before a function that returns the results
 * of an HTTP call.
 * 
 * Example:
 * ```
 * \@Toast({ ... })
 * someFunction() {
 *    return this.http.get('url');
 * }
 * ```
 * 
 * > #### Note: in actual use, there would not be a \ before `@Toast`, it's only there
 * > #### because JSDoc treats at signs as special characters.
 * 
 * ### Usage
 * Minimal example of success toast:
 * ```
 * \@Toast({
 *   success: {
 *     title: 'Success',
 *     message: 'User added'
 *   }
 * })
 * ```
 * 
 * Error Toast would be similar but using the `error` key instead of `success`.
 * 
 * Both `success` and `error` objects can be used at the same time. This will result
 * in a message being shown for 2xx, 4xx, and 5xx responses
 * 
 * You can display the message from the network response on success and error messages,
 * although you'd likely not use this for successful responses. This can be acheived
 * by setting `success.useResponseAsMessage` or `error.useResponseAsMessage` to true.
 * 
 * Example:
 * ```
 * \@Toast({
 *   error: {
 *     title: 'Oops',
 *     useResponseAsMessage: true
 *   }
 * })
 * ```
 * 
 * Both `title` and `message` can change depending on the HTTP status code. Simply
 * set the property to an object, with the keys being the status code you want to target
 * and the value being the text you want for that status code.
 * 
 * Example of using response codes for dynamic titles/messages:
 * ```
 * \@Toast({
 *   error: {
 *     title: {
 *       400: 'Sorry :(',
 *       404: 'Some different title'
 *     },
 *     message: 'Something went wrong'
 *   }
 * })
 * ```
 * 
 * Message can use the message from the response by setting the response code
 * to 'auto'.
 * 
 * Example
 * ```
 * \@Toast({
 *   error: {
 *     title: 'Some title here',
 *     message: {
 *       400: 'auto',
 *       404: 'Some different message'
 *     }
 *   }
 * })
 * ```
 * 
 * Both `title` and `message` can be set to an object at the same time.
 * @param options {@link IToastOptions}
 */
export function Toast(options: IToastOptions) {
  return function(target: any, memberName: string, propertyDescriptor: PropertyDescriptor): PropertyDescriptor {
    // save reference to original function
    const originalMethod = propertyDescriptor.value;

    const responseHandler = (type: keyof IToastOptions, res: HttpErrorResponse) => {
      // get a reference to the toastr service
      const toastr: ToastrService = AppModule.injector.get(ToastrService);

      if(options[type]) {
        let message: string;

        // get message from endpoint response if necessary
        if(options[type].useResponseAsMessage) {
          message = res?.error?.errors?.join(' ');
        } else {
          if(typeof options[type].message === 'string') {
            message = options[type].message as string;
          } else {
            if(options[type].message[res.status] === 'auto') {
              message = res?.error?.errors?.join(' ');
            } else {
              message = options[type].message[res.status];
            }
          }
        }

        let title: string;

        if(typeof options[type].title === 'string') {
          title = options[type].title as string;
        } else {
          title = options[type].title[res.status];
        }

        // display the toast
        const toast = toastr[type](
          message,
          title,
          options[type]
        );

        // keep track of permanent toasts so we can remove them later if necessary
        if(options[type].disableTimeOut) {
          AppModule.toasts.push(toast);
        }
      }
    };
    
    // override original function with ours
    propertyDescriptor.value = function(...args) {
      // apply a pipe to the observable
      return originalMethod.call(this, ...args).pipe(
        tap({
          next: (res: HttpErrorResponse) => responseHandler('success', res),
          error: (err) => responseHandler('error', err)
        })
      );
    };
    
    return propertyDescriptor;
  }
}
